import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import makeStyles from '@mui/styles/makeStyles';
import { Grid, Button, Box, TextField, Typography, Checkbox } from '@mui/material';
import { Formik, Form } from 'formik';

import { DialogTitleWithClose, FileUploader, Loader } from 'ui';
import { addNotificationAction } from 'store/notifications/notificationsActions';

import { createProductThirdPartySource } from '../services';
import { fetchShopProductPropertiesAction } from '../store/shopActions';

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
});

const validationSchema = Yup.object().shape({
  name: Yup.string().required(),
  thirdparty: Yup.boolean(),
});

export const ShopProductSourceModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const [logo, setLogo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (values, { validateForm }) => {
    setIsLoading(true);

    validateForm(values);

    if (!logo) {
      setIsLoading(false);
    }

    const formData = new FormData();

    // eslint-disable-next-line
    for (const field in values) {
      const value = values[field];
      formData.append(field, value);
    }

    formData.append('logo', logo, logo.name);

    try {
      await createProductThirdPartySource(formData);

      dispatch(fetchShopProductPropertiesAction());

      onClose();
    } catch (e) {
      dispatch(
        addNotificationAction({
          variant: 'error',
          message: t('Failed to create a product third party source'),
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitleWithClose onClose={onClose}>{t('Add product source')}</DialogTitleWithClose>
      <DialogContent>
        <div className={classes.root}>
          <Formik
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            validateOnChange={false}
          >
            {({ handleChange, setFieldValue, errors, values }) => (
              <Form>
                <Box width="75%" margin="0 auto">
                  <Grid container>
                    <Grid item xs={12}>
                      <TextField
                        name="name"
                        label={t('Name')}
                        variant="outlined"
                        error={Boolean(errors.name)}
                        onChange={handleChange}
                        fullWidth
                        disabled={isLoading}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container alignItems="center">
                        <Grid item>
                          <Typography variant="body1" component="span">
                            {t('Third party:')}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Checkbox
                            name="thirdparty"
                            onChange={event => setFieldValue('thirdparty', event.target.checked)}
                            disabled={isLoading}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Box mt={2}>
                        <FileUploader onChange={img => setLogo(img[0])} disabled={isLoading} />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Box mt={2}>
                  <Grid container justifyContent="flex-end">
                    {isLoading ? (
                      <Loader />
                    ) : (
                      <Button
                        color="primary"
                        variant="contained"
                        type="submit"
                        disabled={Boolean(!logo || !values.name)}
                      >
                        {t('Add source')}
                      </Button>
                    )}
                  </Grid>
                </Box>
              </Form>
            )}
          </Formik>
        </div>
      </DialogContent>
    </Dialog>
  );
};

ShopProductSourceModal.defaultProps = {
  open: false,
  onClose: () => null,
};

ShopProductSourceModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
